/**
 * Catálogo único de documentos requeridos (ejido vs privado) para Captura
 * paso 3 y Expedientes. Los `id` son la clave estable con backend/BFF.
 *
 * - Listas agrupadas para UI (mismo orden y secciones en ambas pantallas).
 * - `DOCS_EJIDAL` / `DOCS_PRIVADA` son el flatten; usar para conteos y lookups.
 *
 * Notas sobre IDs:
 * - `acreditacion_titularidad_parcela`: paraguas para tenencias atípicas.
 * - `acta_matrimonio` en ejidal alinea con privado (régimen conyugal).
 */

export interface DocDef {
  id: string;
  label: string;
  note: string;
}

export interface DocGroup {
  label: string;
  items: DocDef[];
}

export const DOCS_EJIDAL_GROUPS: DocGroup[] = [
  {
    label: 'Titularidad',
    items: [
      {
        id: 'acreditacion_titularidad_parcela',
        label: 'Documento que acredite titularidad de derechos sobre la parcela',
        note: 'Ej.: certificado parcelario RAN, constancia de derechos del comisariado, resolución de cesión, etc.',
      },
      {
        id: 'certificado_parcelario',
        label: 'Certificado parcelario (RAN)',
        note: 'Emitido por el Registro Agrario Nacional.',
      },
      {
        id: 'constancia_derechos_ejidales',
        label: 'Constancia de derechos ejidales',
        note: 'Emitida por el Comisariado Ejidal.',
      },
    ],
  },
  {
    label: 'Agrario',
    items: [
      {
        id: 'acta_asamblea_delegacion',
        label: 'Acta de asamblea',
        note: 'Autoriza el arrendamiento (Art. 45, Ley Agraria).',
      },
      {
        id: 'constancia_no_adeudos_ejido',
        label: 'Constancia de no adeudos con el ejido',
        note: '',
      },
    ],
  },
  {
    label: 'Identificación y datos personales',
    items: [
      {
        id: 'identificacion_oficial',
        label: 'Credencial de elector (INE)',
        note: 'Vigente al momento de la firma.',
      },
      { id: 'curp', label: 'CURP', note: '' },
      { id: 'comprobante_domicilio', label: 'Comprobante de domicilio', note: '' },
      {
        id: 'rfc_homoclave',
        label: 'RFC con homoclave',
        note: 'No mayor a 6 meses de emisión.',
      },
      { id: 'acta_nacimiento', label: 'Acta de nacimiento', note: '' },
      {
        id: 'acta_matrimonio',
        label: 'Acta de matrimonio',
        note: 'Si aplica (régimen de sociedad conyugal).',
      },
      {
        id: 'datos_bancarios_clabe',
        label: 'Datos bancarios (CLABE)',
        note: 'Carátula bancaria con CLABE interbancaria.',
      },
    ],
  },
];

export const DOCS_PRIVADA_GROUPS: DocGroup[] = [
  {
    label: 'Titularidad',
    items: [
      {
        id: 'acreditacion_titularidad_parcela',
        label: 'Documento que acredite titularidad de derechos sobre la parcela',
        note: 'Ej.: escritura pública, certificado de propiedad RPP, etc.',
      },
      {
        id: 'escritura_publica',
        label: 'Escritura pública',
        note: 'Inscrita en el RPP.',
      },
    ],
  },
  {
    label: 'Predio',
    items: [{ id: 'no_adeudo_predial', label: 'Boleta predial', note: 'Al corriente de pago.' }],
  },
  {
    label: 'Identificación y datos personales',
    items: [
      {
        id: 'identificacion_oficial',
        label: 'Credencial de elector (INE)',
        note: 'Vigente al momento de la firma.',
      },
      { id: 'curp', label: 'CURP', note: '' },
      { id: 'comprobante_domicilio', label: 'Comprobante de domicilio', note: '' },
      {
        id: 'rfc_homoclave',
        label: 'RFC con homoclave',
        note: 'No mayor a 6 meses de emisión.',
      },
      { id: 'acta_nacimiento', label: 'Acta de nacimiento', note: '' },
      {
        id: 'acta_matrimonio',
        label: 'Acta de matrimonio',
        note: 'Si aplica (régimen de sociedad conyugal).',
      },
      {
        id: 'datos_bancarios_clabe',
        label: 'Datos bancarios (CLABE)',
        note: 'Carátula bancaria con CLABE interbancaria.',
      },
    ],
  },
];

export const DOCS_EJIDAL: DocDef[] = DOCS_EJIDAL_GROUPS.flatMap((g) => g.items);
export const DOCS_PRIVADA: DocDef[] = DOCS_PRIVADA_GROUPS.flatMap((g) => g.items);
